import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  SearchFacet,
  SearchTabItem,
  RemoveFilterCallback,
  FacetKeyValuePair,
  ApplyPendingFiltersCallback,
  ApplyPendingFiltersProps,
} from './../../../types/search';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useSearchConfig } from '../../Search/hooks/useSearchConfig';
import { CALENDAR_DATE_FACET, CALENDAR_RESULTS_DATE_FACET } from '../index';

const isItemSelected = (
  facetKey: string,
  itemValue: string,
  itemLabel: string | undefined,
  filters: Record<string, string[]>
): boolean => {
  const filterValues = filters[facetKey] ?? [];
  return filterValues.includes(itemValue) || (itemLabel !== undefined && filterValues.includes(itemLabel));
};

export const usePortalListing = (
  apiUrl: string,
  paginationDisplayLimit: number = 10,
  documentContentTypes?: string,
  prefilterCategoryIds: string[] = [],
  facetKeyForCategories: string = 'pdfcategoryids',
  listingType: string = 'documents'
) => {
  const config = useSearchConfig();
  const initialFacetItemsRef = useRef<Record<string, Array<{ value: string; label?: string }>>>({});
  const dateFacetKeysRef = useRef<Set<string>>(new Set());
  const allSeenStatusItemsRef = useRef<Map<string, { value: string; label?: string }>>(new Map());
  // Set the default sort column based on listing type
  const defaultSortCol: Record<string, string> = {
    services: 'status',
    invoices: 'dateIssued',
    jobs: 'jobDate',
  };

  // Core state
  type SearchParams = {
    page: number;
    sortOrder: string;
    sortCol?: string;
    activeTab: string | null;
    filters: Record<string, string[]>;
    query: string | null;
  };

  const [searchParams, setSearchParams] = useState<SearchParams>({
    page: 1,
    sortOrder: '1',
    sortCol: defaultSortCol[listingType],
    activeTab: listingType ?? null,
    filters: {},
    query: null,
  });

  // UI state
  const [openFacets, setOpenFacets] = useState<Record<string, boolean>>({});
  const [previousResults, setPreviousResults] = useState<any[]>([]);
  const [previousTabs, setPreviousTabs] = useState<any[]>([]);
  const [previousFacets, setPreviousFacets] = useState<any[]>([]);
  const [previousSortByOptions, setPreviousSortByOptions] = useState<any[]>([]);
  const [urlParamsLoaded, setUrlParamsLoaded] = useState(false);

  // Cache last known calendar date value to preserve view mode when filters produce no results
  const lastKnownCalendarDateRef = useRef<string | null>(null);

  const prefilterStrings = useMemo(() => prefilterCategoryIds.map(String), [prefilterCategoryIds]);

  const buildQueryString = useCallback(
    (params: typeof searchParams) => {
      const urlParams = new URLSearchParams();

      urlParams.set('page', params.page.toString());
      urlParams.set('order', params.sortOrder);
      if (params.sortCol) {
        urlParams.set('orderCol', params.sortCol);
      }
      urlParams.set('type', 'content');
      urlParams.set('pagetype', encodeURIComponent(params.activeTab || listingType));

      if (params.query && params.query !== '') {
        urlParams.set('query', params.query);
      }

      if (documentContentTypes && config.documentAssetTypeGuid) {
        urlParams.set(config.documentAssetTypeGuid, documentContentTypes);
      }

      const effective: Record<string, string[]> = structuredClone(params.filters ?? {});

      const userSelected = effective[facetKeyForCategories] ?? [];
      if (prefilterStrings.length > 0) {
        if (userSelected.length > 0) {
          const allowed = new Set(prefilterStrings);
          effective[facetKeyForCategories] = userSelected.filter(v => allowed.has(v));
        } else {
          effective[facetKeyForCategories] = [...prefilterStrings];
        }
      }

      for (const [key, values] of Object.entries(effective)) {
        if (Array.isArray(values) && values.length > 0) {
          urlParams.set(key, values.join(','));
        }
      }

      // This 'fake' facet gets set to null elsewhere in the app, this ensures
      // it gets removed from the query string when not in use.
      if (urlParams.get(CALENDAR_RESULTS_DATE_FACET) === '') {
        urlParams.delete(CALENDAR_RESULTS_DATE_FACET);
      }

      return `?${urlParams.toString()}`;
    },
    [listingType, documentContentTypes, config.documentAssetTypeGuid, facetKeyForCategories, prefilterStrings]
  );

  const updateUrl = useCallback(
    (params: typeof searchParams) => {
      const queryString = buildQueryString(params);
      globalThis.history.pushState({}, '', queryString);
    },
    [buildQueryString]
  );

  useEffect(() => {
    const urlParams = new URLSearchParams(globalThis.location.search);
    const initialParams = { ...searchParams };

    const urlPage = urlParams.get('page');
    if (urlPage) initialParams.page = Number.parseInt(urlPage, 10);

    const urlSort = urlParams.get('order');
    if (urlSort) initialParams.sortOrder = urlSort;

    const urlSortCol = urlParams.get('orderCol');
    if (urlSortCol) initialParams.sortCol = urlSortCol;

    const query = urlParams.get('query');
    if (query) initialParams.query = query;

    const urlFilters: Record<string, string[]> = {};
    urlParams.forEach((value, key) => {
      if (!['page', 'order', 'orderCol', 'type', 'pagetype'].includes(key) && key !== config.documentAssetTypeGuid) {
        urlFilters[key] = value.split(',');
      }
    });
    initialParams.filters = urlFilters;

    setSearchParams(initialParams);
    setUrlParamsLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.documentAssetTypeGuid]);

  const listingQuery = useQuery({
    queryKey: [
      'documentsListing',
      {
        page: searchParams.page,
        sort: searchParams.sortOrder,
        sortCol: searchParams.sortCol,
        query: searchParams.query,
        filters: JSON.stringify(searchParams.filters),
      },
    ],
    queryFn: async () => {
      const queryString = buildQueryString(searchParams);
      const apiParams = new URLSearchParams(queryString.substring(1));
      apiParams.set('pageSize', paginationDisplayLimit.toString());
      const apiQueryString = `?${apiParams.toString()}`;

      const url = `${apiUrl}${apiQueryString}`;
      const response = await fetch(url);

      if (!response.ok) throw new Error('Document listing fetch failed');
      return await response.json();
    },
    staleTime: 60000,
    enabled: urlParamsLoaded,
    placeholderData: keepPreviousData,
  });

  const trackDateFacet = useCallback((facet: SearchFacet) => {
    if (facet.type === 'date') {
      dateFacetKeysRef.current.add(facet.key);
    }
  }, []);

  const buildFacetMap = useCallback(
    (facets: SearchFacet[]) => {
      const map: Record<string, Array<{ value: string; label?: string }>> = {};
      facets.forEach(facet => {
        map[facet.key] = (facet.items ?? []).map(item => ({
          value: String(item.value),
          label: item.label,
        }));
        trackDateFacet(facet);
      });
      return map;
    },
    [trackDateFacet]
  );

  const initializeFacets = useCallback(
    (facets: SearchFacet[]) => {
      initialFacetItemsRef.current = buildFacetMap(facets);
    },
    [buildFacetMap]
  );

  const shouldInitializeFacets = useCallback(() => {
    return (
      !listingQuery.isLoading && listingQuery.data?.facets && Object.keys(initialFacetItemsRef.current).length === 0
    );
  }, [listingQuery.isLoading, listingQuery.data]);

  const updatePreviousData = useCallback(() => {
    if (listingQuery.isLoading) return;

    if (listingQuery.data?.results) {
      setPreviousResults(listingQuery.data.results);
    }
    if (listingQuery.data?.tabs) {
      setPreviousTabs(listingQuery.data.tabs);
    }
    // Only update previousFacets when we have results - this preserves filter structure
    // when a filter combination returns 0 results with empty/missing facets from API
    if (listingQuery.data?.facets && listingQuery.data.facets.length > 0 && listingQuery.data?.results?.length > 0) {
      setPreviousFacets(listingQuery.data.facets);
    }
    if (listingQuery.data?.sortByOptions) {
      setPreviousSortByOptions(listingQuery.data.sortByOptions);
    }
  }, [listingQuery.isLoading, listingQuery.data]);

  useEffect(() => {
    if (shouldInitializeFacets()) {
      initializeFacets(listingQuery.data.facets as SearchFacet[]);
    }

    updatePreviousData();
  }, [listingQuery.isLoading, listingQuery.data, initializeFacets, shouldInitializeFacets, updatePreviousData]);

  // Clear previous/cached data on error to avoid showing stale data
  useEffect(() => {
    if (listingQuery.isError) {
      setPreviousResults([]);
      setPreviousTabs([]);
      setPreviousFacets([]);
      setPreviousSortByOptions([]);
      allSeenStatusItemsRef.current.clear();
      lastKnownCalendarDateRef.current = null;
    }
  }, [listingQuery.isError]);

  const facets = useMemo(() => {
    // Return empty array on error to avoid showing stale facets
    if (listingQuery.isError) return [];

    // Use API facets when available and non-empty, fall back to previousFacets
    // This preserves filter state when API returns empty facets with zero results
    const apiFacets = listingQuery.data?.facets;
    const source = apiFacets && apiFacets.length > 0 ? apiFacets : previousFacets;
    if (!source || source.length === 0) return [];

    const processedFacets = source
      .filter((facet: SearchFacet) => facet.key !== 'tab')
      .map((facet: SearchFacet) => {
        const currentByValue = new Map((facet.items ?? []).map(i => [String(i.value), i]));

        // Date range does not need processing in the same way as other facets
        if (facet.key === 'dateRange' || facet.type === 'date') {
          return facet;
        }

        // For status facet, accumulate all items ever seen
        if (facet.key.toLowerCase() === 'status') {
          (facet.items ?? []).forEach(item => {
            if (!allSeenStatusItemsRef.current.has(String(item.value))) {
              allSeenStatusItemsRef.current.set(String(item.value), {
                value: String(item.value),
                label: item.label,
              });
            }
          });
        }

        const baseline =
          initialFacetItemsRef.current[facet.key] ??
          (facet.items ?? []).map(i => ({ value: String(i.value), label: i.label }));

        // For status, use all ever-seen items as baseline instead of just current or initial
        const itemsToMerge =
          facet.key.toLowerCase() === 'status' ? Array.from(allSeenStatusItemsRef.current.values()) : baseline;

        const mergedItems = itemsToMerge.map(b => {
          const curr = currentByValue.get(b.value);
          // Check both value and label to handle URLs that use either
          const isSelected = isItemSelected(facet.key, b.value, b.label, searchParams.filters);

          return {
            ...(curr ?? { value: b.value, label: b.label, total: 0 }),
            isSelected,
          };
        });
        // Don't filter out items with zero results - keep all items so users can adjust filters

        return { ...facet, isOpen: openFacets[facet.key] || false, items: mergedItems };
      });

    // Ensure status facet always exists by reconstructing it from all-seen items if missing
    const hasStatusFacet = processedFacets.some((f: SearchFacet) => f.key.toLowerCase() === 'status');
    if (!hasStatusFacet && allSeenStatusItemsRef.current.size > 0) {
      const statusItems = Array.from(allSeenStatusItemsRef.current.values()).map(b => {
        const isSelected = isItemSelected('status', b.value, b.label, searchParams.filters);
        return {
          value: b.value,
          label: b.label,
          total: 0,
          isSelected,
        };
      });
      processedFacets.push({
        key: 'status',
        items: statusItems,
        isOpen: openFacets['status'] || false,
        type: 'facet',
      } as SearchFacet);
    }

    return processedFacets;
  }, [listingQuery.data?.facets, listingQuery.isError, previousFacets, openFacets, searchParams.filters]);

  // Note: facets already contains isSelected and isOpen state, no need to recompute

  // Action handlers
  const toggleFacet = useCallback((facetKey: string) => {
    setOpenFacets(prev => {
      const isCurrentlyOpen = prev[facetKey];

      if (isCurrentlyOpen) {
        return { ...prev, [facetKey]: false };
      }

      const newState: Record<string, boolean> = {};
      Object.keys(prev).forEach(key => {
        newState[key] = false;
      });

      newState[facetKey] = true;
      return newState;
    });
  }, []);

  const applyPendingFilters: ApplyPendingFiltersCallback = useCallback(
    (pendingFilters: ApplyPendingFiltersProps) => {
      setSearchParams(prev => {
        const newFilters = { ...prev.filters };
        Object.entries(pendingFilters).forEach(([key, values]) => {
          newFilters[key] = Array.isArray(values) ? values : [values];
        });
        const next = { ...prev, filters: newFilters, page: 1 };

        // Update URL with error handling
        try {
          updateUrl(next);
        } catch (error) {
          console.error('Failed to update URL:', error);
        }

        return next; // triggers React Query refetch via queryKey
      });
    },
    [updateUrl]
  );

  const toggleFilter = useCallback(
    (facetKey: string, itemValue: string) => {
      setSearchParams(prev => {
        const newFilters = { ...prev.filters };
        const values = [...(newFilters[facetKey] ?? [])];
        const i = values.indexOf(itemValue);
        if (i >= 0) values.splice(i, 1);
        else values.push(itemValue);
        if (values.length) newFilters[facetKey] = values;
        else delete newFilters[facetKey];

        const next = { ...prev, filters: newFilters, page: 1 };
        updateUrl(next); // updates URL
        return next; // triggers React Query refetch via queryKey
      });
    },
    [updateUrl]
  );

  const clearAllFilters = useCallback(() => {
    // Preserve dateFrom and dateTo only for calendar view (Invoices) as they are controlled by calendar cards
    const preservedFilters: Record<string, string[]> = {};

    const newParams = {
      ...searchParams,
      filters: preservedFilters,
      page: 1,
    };

    setSearchParams(newParams);

    const urlParams = new URLSearchParams();
    urlParams.set('page', '1');
    urlParams.set('order', newParams.sortOrder);
    urlParams.set('type', 'document');

    if (documentContentTypes && config.documentAssetTypeGuid) {
      urlParams.set(config.documentAssetTypeGuid, documentContentTypes);
    }

    globalThis.history.pushState({}, '', `?${urlParams.toString()}`);

    listingQuery.refetch();
  }, [searchParams, listingQuery, documentContentTypes, config.documentAssetTypeGuid]);

  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = { ...searchParams, page };
      setSearchParams(newParams);
      updateUrl(newParams);
    },
    [searchParams, updateUrl]
  );

  const handleSortChange = useCallback(
    (newSortOrder: string, newSortCol: string) => {
      const newParams = { ...searchParams, sortOrder: newSortOrder, sortCol: newSortCol, page: 1 };
      setSearchParams(newParams);
      updateUrl(newParams);
    },
    [searchParams, updateUrl]
  );

  const removeFilter: RemoveFilterCallback = useCallback(
    (props: FacetKeyValuePair) => {
      // Prevent removal of dateFrom and dateTo only for calendar view (Invoices) as they are controlled by calendar cards
      const isCalendarView = listingType === 'jobs';
      if (isCalendarView && (props.facetKey === 'dateFrom' || props.facetKey === 'dateTo')) {
        return;
      }

      setSearchParams(prev => {
        const newFilters = structuredClone(prev.filters);

        if (newFilters[props.facetKey]) {
          // For date filters, itemValue is a comma-separated string "2024-11-01,2024-11-30"
          // but it's stored as an array ["2024-11-01", "2024-11-30"]
          // So we need to check if the joined array matches the itemValue
          const currentValues = newFilters[props.facetKey];
          const joinedValue = Array.isArray(currentValues) ? currentValues.join(',') : currentValues;

          if (joinedValue === props.itemValue) {
            // Remove the entire filter
            delete newFilters[props.facetKey];
          } else {
            // For non-date filters, filter out the specific value
            const values = currentValues.filter((v: string) => v !== props.itemValue);

            if (values.length) {
              newFilters[props.facetKey] = values;
            } else {
              delete newFilters[props.facetKey];
            }
          }
        }

        const newParams = {
          ...prev,
          filters: newFilters,
          page: 1,
        };

        const urlParams = new URLSearchParams();
        urlParams.set('page', '1');
        urlParams.set('order', newParams.sortOrder);
        urlParams.set('type', 'document');

        if (documentContentTypes && config.documentAssetTypeGuid) {
          urlParams.set(config.documentAssetTypeGuid, documentContentTypes);
        }

        Object.entries(newFilters).forEach(([key, values]) => {
          if (Array.isArray(values) && values.length > 0) {
            urlParams.set(key, values.join(','));
          }
        });

        globalThis.history.pushState({}, '', `?${urlParams.toString()}`);

        return newParams;
      });
    },
    [documentContentTypes, config.documentAssetTypeGuid, listingType]
  );

  const tabsFromApi = useMemo(
    () => listingQuery.data?.tabs ?? listingQuery.data?.summaryTabs ?? [],
    [listingQuery.data?.tabs, listingQuery.data?.summaryTabs]
  );
  const sortOptions = useMemo(() => {
    if (tabsFromApi.length > 0) {
      const currentTab = tabsFromApi.find(
        (tab: any) => tab.isSelected === true || tab.key === (searchParams.activeTab ?? 'documents')
      );
      if (currentTab?.sortOptions?.length) return currentTab.sortOptions;
    }
    return listingQuery.data?.sortOptions ?? [];
  }, [tabsFromApi, listingQuery.data?.sortOptions, searchParams.activeTab]);

  const tabs = useMemo(() => {
    if (!listingQuery.data?.tabs && previousTabs.length > 0) {
      return previousTabs.map((tab: SearchTabItem) => ({
        ...tab,
        isSelected: tab.key === searchParams.activeTab,
      }));
    }

    if (!listingQuery.data?.tabs) return [];

    return listingQuery.data.tabs.map((tab: SearchTabItem) => ({
      ...tab,
      isSelected: tab.key === searchParams.activeTab,
    }));
  }, [listingQuery.data?.tabs, searchParams.activeTab, previousTabs]);

  const filteredResults = useMemo(() => {
    if (listingQuery.isLoading && previousResults.length > 0) {
      return previousResults;
    }

    if (listingQuery.data?.results) {
      return listingQuery.data.results;
    }

    return [];
  }, [listingQuery.data?.results, listingQuery.isLoading, previousResults]);

  // Simple computation, no need for useMemo overhead
  // Exclude date-related keys for calendar view as they are controlled by calendar cards
  const isCalendarView = listingType === 'jobs';
  const dateFilterKeys = new Set(['dateFrom', 'dateTo']);
  if (CALENDAR_DATE_FACET) {
    dateFilterKeys.add(CALENDAR_DATE_FACET);
  }
  if (CALENDAR_RESULTS_DATE_FACET) {
    dateFilterKeys.add(CALENDAR_RESULTS_DATE_FACET);
  }
  const hasFiltersApplied = isCalendarView
    ? Object.keys(searchParams.filters).some(key => !dateFilterKeys.has(key))
    : Object.keys(searchParams.filters).length > 0;

  const updateQuery = useCallback(
    (searchTerm: string) => {
      setSearchParams(prev => {
        const next = { ...prev, query: searchTerm, page: 1 };

        // Update URL with error handling
        try {
          updateUrl(next);
        } catch (error) {
          console.error('Failed to update URL:', error);
        }

        return next; // triggers React Query refetch via queryKey
      });
    },
    [updateUrl]
  );

  // Memoize the calendar date facet for calendar view components
  // Priority: user's filter selection > API facet > last known value
  // This ensures the UI always reflects what the user requested, even with 0 results
  const calendarDateFacet = useMemo(() => {
    if (!CALENDAR_DATE_FACET) {
      return null;
    }

    const facet = facets.find((f: SearchFacet) => f.key === CALENDAR_DATE_FACET);

    // Cache API facet value when available (for initial/default view)
    if (facet && facet.items?.length > 0) {
      const dateValues = facet.items.map((item: { value: string }) => item.value);
      lastKnownCalendarDateRef.current = dateValues.join(',');
    }

    // Priority 1: searchParams.filters - always reflects user's requested date range
    // This is the source of truth for what the user wants to see, regardless of results
    const filterDates = searchParams.filters[CALENDAR_DATE_FACET];
    if (filterDates && filterDates.length > 0) {
      const selectedValue = filterDates.join(',');
      return {
        selectedValue,
        items: filterDates.map(value => ({ value, label: value, total: 0 })),
        facet: facet ?? null,
      };
    }

    // Priority 2: API facet data (for initial load when no filters are applied yet)
    if (facet && facet.items?.length > 0) {
      const dateValues = facet.items.map((item: { value: string }) => item.value);
      const selectedValue = dateValues.join(',');
      return {
        selectedValue,
        items: facet.items,
        facet,
      };
    }

    // Priority 3: last known value (preserves view when API returns empty due to filters)
    if (lastKnownCalendarDateRef.current) {
      const dates = lastKnownCalendarDateRef.current.split(',');
      return {
        selectedValue: lastKnownCalendarDateRef.current,
        items: dates.map(value => ({ value, label: value, total: 0 })),
        facet: null,
      };
    }

    return null;
  }, [facets, searchParams.filters]);

  return {
    // Dynamic data for extensible table/filter rendering
    rows: filteredResults,
    filters: listingQuery.data?.filters ?? [],
    sortByOptions: listingQuery.data?.sortByOptions ?? previousSortByOptions,

    // Core state and derived state
    activeTabKey: searchParams.activeTab,
    sortOrder: searchParams.sortOrder,

    // React Query states
    isLoading: listingQuery.isLoading,
    isError: listingQuery.isError,
    isSuccess: listingQuery.isSuccess,
    isFetching: listingQuery.isFetching,

    // UI state
    facets: facets,
    appliedFilters: facets,
    requestedFilters: searchParams.filters,
    tabs,
    sortOptions,
    pagination: {
      currentPage: searchParams.page,
      totalPages: listingQuery.data?.pagination?.totalPages ?? 1,
    },
    summary: {
      total: listingQuery.data?.summary?.total ?? null,
      sortCol: listingQuery.data?.summary?.sortCol ?? null,
      sortOrder: searchParams.sortOrder ?? '1',
    },
    search: {
      query: searchParams.query,
      updateQuery,
    },

    // Actions
    toggleFacet,
    toggleFilter,
    removeFilter,
    clearAllFilters,
    handlePageChange,
    handleSortChange,
    applyPendingFilters,

    // Filter state
    hasFiltersApplied,

    // Calendar date facet
    calendarDateFacet,
  };
};
